import { ArrowRightIcon, CheckIcon } from '@heroicons/react/solid'
import Link from 'components/base/Link'
import Container from 'components/landing/Container'
import { FC } from 'lib/component-utils'
import { useIntl } from 'lib/intl-utils'

const contentByLang = {
  ru: {
    eyebrow: 'Автоматизация',
    heading: 'Управление ценами',
    description:
      'Контролируйте цены на Wildberries и Ozon без постоянной ручной работы',
    features: [
      {
        name: 'Контроль РРЦ',
        description: 'с учётом скидок маркетплейса и цены, которую видит покупатель',
      },
      {
        name: 'Гибкие сценарии',
        description: 'по ценам конкурентов, остаткам, расписанию и условиям акций',
      },
      {
        name: 'Расчёт по маржинальности',
        description: 'с учётом себестоимости, налогов и расходов маркетплейса',
      },
    ],
    cta: 'Подробнее об управлении ценами',
    imageAlt: 'Отчёт репрайсера: история цен товара и причины изменений',
  },
  en: {
    eyebrow: 'Automation',
    heading: 'Price management',
    description:
      'Control prices on Wildberries and Ozon without constant manual work',
    features: [
      {
        name: 'Recommended retail price control',
        description: 'accounting for marketplace discounts and the price buyers see',
      },
      {
        name: 'Flexible scenarios',
        description: 'based on competitor prices, stock levels, schedules and promotion terms',
      },
      {
        name: 'Margin-based pricing',
        description: 'accounting for product costs, taxes and marketplace expenses',
      },
    ],
    cta: 'Explore price management',
    imageAlt: 'Repricer report: product price history and reasons for price changes',
  },
} as const

const PriceManagement: FC = () => {
  const { lang } = useIntl()
  const content = contentByLang[lang as 'en' | 'ru']

  return (
    <Container className="!pt-px overflow-hidden relative isolate">
      <div className="grid grid-cols-1 gap-x-8 gap-y-16 mx-auto max-w-2xl sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2">
        <div className="lg:pr-8 lg:pt-4">
          <div className="lg:max-w-lg">
            <p className="text-base font-semibold leading-7 text-primary-600">{content.eyebrow}</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
              {content.heading}
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">{content.description}</p>
            <dl className="mt-10 space-y-8 max-w-xl text-base leading-7 text-gray-600 lg:max-w-none">
              {content.features.map((feature) => (
                <div key={feature.name} className="relative pl-9">
                  <dt className="inline font-medium text-gray-900">
                    <CheckIcon
                      className="absolute top-1 left-1 w-5 h-5 text-primary-500"
                      aria-hidden="true"
                    />
                    {feature.name}
                  </dt>{' '}
                  <dd className="inline">{feature.description}</dd>
                </div>
              ))}
            </dl>
            <Link
              href="/repricer/"
              className="inline-block mt-8 text-base font-semibold text-primary-600 hover:text-primary-700"
              innerClassName="inline-flex items-center gap-2"
            >
              <span>{content.cta}</span>
              <ArrowRightIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
            </Link>
          </div>
        </div>
        <img
          src="/next/img/landing/sellematics/repricer-wb.png"
          alt={content.imageAlt}
          className="w-full max-w-none rounded-xl shadow-xl ring-1 ring-gray-400/10 sm:w-[48rem] lg:w-[57rem]"
          width={1800}
          height={1095}
          loading="lazy"
        />
      </div>
      <div
        className="absolute hidden sm:block inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl"
        aria-hidden="true"
      >
        <div
          className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-[#ff5a39] to-[#fcc63c] opacity-20 sm:left-[calc(50%+24rem)] sm:w-[72.1875rem]"
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
        />
      </div>
    </Container>
  )
}

export default PriceManagement
